/**
 * Pagamentos Não Identificados — a ponte da conciliação para a tela de Títulos.
 *
 * Dinheiro entrou e ninguém sabe a que título pertence. Antes, descobrir era copiar o valor do
 * pagamento, fechar, abrir os filtros, escolher a coluna, escolher o operador e colar. Aqui cada
 * célula tem uma lupa que faz esse caminho em um clique.
 *
 * ⚠️ A lupa NÃO aplica direto. Pede confirmação porque as duas intenções possíveis são opostas e
 * indistinguíveis do clique: "só quero ver isto" (limpa o resto) e "quero cruzar com o que já
 * filtrei" (mantém). Escolher uma por padrão apagaria silenciosamente o trabalho de quem quis a
 * outra.
 */
import { useCallback, useEffect, useState } from 'react';
import { FaInfoCircle, FaSearch, FaTimes } from 'react-icons/fa';

import { PaymentRow, listPaymentsByStatus } from '../services/billingApi';
import {
    ShortcutField, shortcutDescription, filterFromPayment,
} from '../constants/chargeFilterFromPayment';
import { AppliedFilter } from '../../../molecules/FilterBuilder/types';
import { paymentMethodLabel, localDate, availableOf } from '../../../molecules/payment/PendingPaymentPicker';
import { formatBRL } from '../../../../utils/currencyUtils';
import { formatTaxId } from '../../../../utils/taxIdUtils';
import RefreshButton from '../../../atoms/RefreshButton';
import PaymentInfoModal from './PaymentInfoModal';
import CommentCell from '../../../molecules/CommentCell';
import PaymentMarkingHistoryButton from '../../../molecules/payment/PaymentMarkingHistory';

const TH = 'px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-300';
const TD = 'px-3 py-2 text-sm align-top';

/** O que o usuário escolheu no dialog de confirmação. */
export type ApplyMode = 'ONLY' | 'ADD';

export const TOOLTIP_ONLY =
    'Limpa os filtros atuais e deixa apenas este — para olhar só o que casa com este pagamento';
export const TOOLTIP_ADD =
    'Mantém os filtros que já estão valendo e soma este — para cruzar com a busca atual';

interface Props {
    onClose: () => void;
    /**
     * Aplica o filtro escolhido. `ONLY` fecha tudo (a tela já está filtrada, não há mais o que
     * fazer aqui); `ADD` mantém este modal aberto, porque acrescentar costuma vir em série.
     */
    onApplyFilter: (filter: AppliedFilter, mode: ApplyMode) => void;
}

/** Célula com a lupa ao lado do valor. Sem dado, sem lupa: não há o que filtrar. */
const WithMagnifier: React.FC<{
    bodyText: React.ReactNode;
    available: boolean;
    label: string;
    onClick: () => void;
}> = ({ bodyText, available, label, onClick }) => (
    <div className="flex items-start gap-1.5">
        <span className="flex-1">{bodyText}</span>
        {available && (
            <button onClick={onClick} aria-label={label} title={`Filtrar títulos: ${label}`}
                className="mt-0.5 text-gray-400 hover:text-blue-600 shrink-0">
                <FaSearch size={11} />
            </button>
        )}
    </div>
);

const UnidentifiedPaymentsModal: React.FC<Props> = ({ onClose, onApplyFilter }) => {
    const [rows, setRows] = useState<PaymentRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    /** o atalho aguardando confirmação — `null` = nenhum dialog aberto */
    const [pendingFilter, setPendingFilter] = useState<{ filter: AppliedFilter; field: ShortcutField } | null>(null);
    /**
     * Pagamento cujas informações estão abertas. É por aqui que se lê a OBSERVAÇÃO de quem
     * registrou — a única pista escrita sobre a origem do dinheiro, e o que mais ajuda quando
     * nenhuma das lupas acha o título.
     */
    const [infoPayment, setInfoPayment] = useState<PaymentRow | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            setRows(await listPaymentsByStatus('PENDING'));
        } catch (e: any) {
            setError(e?.message || 'Falha ao carregar os pagamentos');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const askConfirmation = (payment: PaymentRow, field: ShortcutField) => {
        const filter = filterFromPayment(payment, field);
        if (filter) setPendingFilter({ filter, field });
    };

    const confirm = (mode: ApplyMode) => {
        if (!pendingFilter) return;
        onApplyFilter(pendingFilter.filter, mode);
        setPendingFilter(null);
        // ONLY fecha tudo: a tela já está filtrada e não há mais nada a fazer aqui. ADD segue
        // aberto porque acrescentar costuma vir em série — data, depois valor, depois pagador.
        if (mode === 'ONLY') onClose();
    };

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
                    <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-600">
                        <div>
                            <h3 className="text-lg font-semibold">Pagamentos Não Identificados</h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Dinheiro que já entrou e ainda não foi ligado a nenhuma fatura. Use
                                as lupas para procurar o título correspondente.
                            </p>
                        </div>
                        <button onClick={onClose} aria-label="Fechar"
                            className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                    </div>

                    <div className="flex-1 overflow-auto px-6 py-4">
                        {loading && <p className="text-sm text-gray-500 py-6 text-center">Carregando pagamentos...</p>}

                        {!loading && error && (
                            <div className="py-6 text-center">
                                <p className="text-sm text-red-600">{error}</p>
                                <button onClick={load} className="mt-2 text-sm text-blue-600 hover:underline">
                                    Tentar de novo
                                </button>
                            </div>
                        )}

                        {!loading && !error && rows.length === 0 && (
                            <div className="py-8 text-center">
                                <p className="text-sm text-gray-500">
                                    Nenhum pagamento pendente de identificação.
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                    Todo o dinheiro que entrou já está ligado a alguma fatura.
                                </p>
                            </div>
                        )}

                        {!loading && !error && rows.length > 0 && (
                            <>
                                <div className="flex items-center justify-between mb-1">
                                    <p className="text-xs text-gray-500">
                                        {rows.length === 1 ? '1 pagamento' : `${rows.length} pagamentos`}
                                    </p>
                                    <RefreshButton label="os pagamentos não identificados" busy={loading} onClick={load} />
                                </div>
                                <div className="border dark:border-slate-600 rounded overflow-hidden">
                                    <table className="w-full">
                                        <thead className="bg-gray-50 dark:bg-slate-700">
                                            <tr>
                                                <th className={TH}>Data</th>
                                                {/* Pendente antes do total: é o que ainda falta
                                                    identificar naquele dinheiro, e é por ele que
                                                    este modal existe. A LUPA continua no total —
                                                    quem procura o título cruza pelo valor que caiu
                                                    no banco, não pelo saldo da identificação. */}
                                                <th className={`${TH} text-right`}>Pendente</th>
                                                <th className={`${TH} text-right`}>Total</th>
                                                <th className={TH}>Meio</th>
                                                <th className={TH}>Nome Pagador</th>
                                                <th className={TH}>Doc. Pagador</th>
                                                <th className={TH}>Mensagem</th>
                                                <th className={`${TH} text-right`}>Info</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map(row => (
                                                <tr key={row.paymentId} className="border-t dark:border-slate-600">
                                                    <td className={TD}>
                                                        <WithMagnifier
                                                            bodyText={localDate(row.paymentDate)}
                                                            available={!!row.paymentDate}
                                                            label={shortcutDescription('date')}
                                                            onClick={() => askConfirmation(row, 'date')} />
                                                    </td>
                                                    <td className={`${TD} text-right font-semibold`}>
                                                        <WithMagnifier
                                                            bodyText={formatBRL(availableOf(row))}
                                                            available={row.amount != null}
                                                            label={shortcutDescription('pendingAmount')}
                                                            onClick={() => askConfirmation(row, 'pendingAmount')} />
                                                    </td>
                                                    <td className={`${TD} text-right text-gray-500`}>
                                                        <WithMagnifier
                                                            bodyText={formatBRL(row.amount || 0)}
                                                            available={row.amount != null}
                                                            label={shortcutDescription('amount')}
                                                            onClick={() => askConfirmation(row, 'amount')} />
                                                    </td>
                                                    {/* Meio NÃO tem lupa: não estreita nada útil —
                                                        quase todo pagamento é PIX, e o título não
                                                        guarda por qual meio será pago */}
                                                    <td className={TD}>{paymentMethodLabel(row)}</td>
                                                    <td className={TD}>
                                                        <WithMagnifier
                                                            bodyText={row.payer?.name || '—'}
                                                            available={!!row.payer?.name}
                                                            label={shortcutDescription('payerName')}
                                                            onClick={() => askConfirmation(row, 'payerName')} />
                                                    </td>
                                                    <td className={TD}>
                                                        <WithMagnifier
                                                            bodyText={row.payer?.documentNumber
                                                                ? formatTaxId(row.payer.documentNumber) : '—'}
                                                            available={!!row.payer?.documentNumber}
                                                            label={shortcutDescription('payerDocument')}
                                                            onClick={() => askConfirmation(row, 'payerDocument')} />
                                                    </td>
                                                    {/* sem lupa: não é dado de busca, é o resto
                                                        do pagamento — observação, conta, recibo,
                                                        quem registrou */}
                                                    <td className={`${TD} max-w-[220px]`}>
                                                        <CommentCell notes={row.notesList || []} />
                                                    </td>
                                                    <td className={`${TD} text-right whitespace-nowrap`}>
                                                        <PaymentMarkingHistoryButton paymentId={row.paymentId}
                                                            zIndexClass="z-[60]" />
                                                        <button onClick={() => setInfoPayment(row)}
                                                            aria-label={`Informações do pagamento de ${formatBRL(row.amount || 0)}`}
                                                            title="Mais informações deste pagamento"
                                                            className="text-gray-400 hover:text-blue-600 align-middle">
                                                            <FaInfoCircle />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="px-6 py-3 border-t dark:border-slate-600 flex justify-end">
                        <button onClick={onClose}
                            className="px-4 py-2 text-sm bg-gray-200 dark:bg-slate-600 rounded hover:bg-gray-300">
                            Fechar
                        </button>
                    </div>
                </div>
            </div>

            {/* z-[60]: acima deste modal (z-50) e abaixo do dialog de confirmação (z-[70]), para
                a ordem ficar definida mesmo se os dois estiverem montados */}
            {infoPayment && (
                <PaymentInfoModal payment={infoPayment} zIndexClass="z-[60]"
                    onClose={() => setInfoPayment(null)} />
            )}

            {/* z acima deste modal (z-50): abrindo por baixo, o usuário só veria a tela escurecer */}
            {pendingFilter && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-md w-full">
                        <h3 className="text-base font-semibold mb-2">Filtrar títulos por este valor?</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                            {shortcutDescription(pendingFilter.field)}:{' '}
                            <strong className="break-all">{readableValue(pendingFilter.filter)}</strong>
                        </p>

                        <div className="flex flex-wrap gap-2 justify-end mt-5">
                            <button onClick={() => setPendingFilter(null)}
                                className="px-4 py-2 text-sm rounded bg-gray-200 dark:bg-slate-600 hover:bg-gray-300">
                                Cancelar
                            </button>
                            <button onClick={() => confirm('ADD')} title={TOOLTIP_ADD}
                                className="px-4 py-2 text-sm rounded bg-slate-600 text-white hover:bg-slate-700">
                                Acrescentar
                            </button>
                            <button onClick={() => confirm('ONLY')} title={TOOLTIP_ONLY}
                                className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700">
                                Somente Este
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

/** O valor do filtro como o usuário o lê — intervalo de ponta igual aparece como um valor só. */
export const readableValue = (filter: AppliedFilter): string => {
    if (!Array.isArray(filter.value)) return String(filter.value);
    const [from, to] = filter.value;
    return from === to ? String(from) : `${from} até ${to}`;
};

export default UnidentifiedPaymentsModal;
